import type { IntelReportCall, IntelReportScorecard, IntelReportTarget } from '@einhaus/project314-types';

export const horizons = [1, 7, 30, 90, 180, 365] as const;
export const cohortChoices = [
    { value: 'hindsight_free', label: 'Live, model cutoff before publication' },
    { value: 'live_captured', label: 'All live capture' },
    { value: 'all', label: 'Live + historical backfill' }
] as const;
export const basisChoices = [
    { value: 'market', label: 'Against SPY' },
    { value: 'timing', label: 'Against random entries in the same stock' }
] as const;
export const directionChoices = [
    { value: 'all', label: 'Both directions' },
    { value: 'bullish', label: 'Bullish' },
    { value: 'bearish', label: 'Bearish' }
] as const;
export const captureChoices = [
    { value: 'all', label: 'All captured posts' },
    { value: 'live', label: 'Captured live' },
    { value: 'backfill', label: 'Historical backfill' }
] as const;
export const kindChoices = [
    { value: 'all', label: 'All claim types' },
    { value: 'forecast', label: 'Forecasts' },
    { value: 'recommendation', label: 'Recommendations' },
    { value: 'trade_claim', label: 'Reported trades' },
    { value: 'holding_claim', label: 'Reported holdings' },
    { value: 'retrospective', label: 'Looking back' }
] as const;

export const words = (value: string) => value.replaceAll('_', ' ');
export const percent = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);
export const edge = (value: number | null) => (value === null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(2)} pp`);
export const eastern = (unix: number | null) =>
    unix === null
        ? '—'
        : new Intl.DateTimeFormat(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
              timeZone: 'America/New_York'
          }).format(unix * 1000);

export const skillLabel: Record<IntelReportScorecard['skillClass'], string> = {
    skilled: 'Positive evidence',
    unskilled: 'No clear edge',
    antiskilled: 'Negative evidence',
    insufficient: 'Not enough samples'
};

export const callOutcomeLabel = (call: IntelReportCall): string => {
    if (call.outcome)
        return call.outcome.directionalExcessPercent === null ? words(call.outcome.status) : edge(call.outcome.directionalExcessPercent);
    if (call.statementKind === 'retrospective') return 'Looking back; unscored';
    if (call.conditionKind !== null) return 'Conditional; see price targets';
    if (call.sampleEventId !== null) return `Grouped with call #${call.sampleEventId}`;
    return 'No scored directional episode';
};

/** Keep the authored units and range; option premium levels never become stock prices. */
export const targetLabel = (target: IntelReportTarget): string => {
    let level: string;
    if (target.targetKind === 'percent_move')
        level = `${target.percentMove ?? '—'}% from ${words(target.percentReference ?? 'unspecified base')}`;
    else if (target.targetKind === 'named_level')
        level = `${words(target.namedLevel ?? 'named level')}${target.namedLevelParameter === null ? '' : ` (${target.namedLevelParameter})`}`;
    else level = target.priceLow === target.priceHigh ? (target.priceLow ?? '—') : `${target.priceLow ?? '—'}–${target.priceHigh ?? '—'}`;
    return `${words(target.targetRole)}: ${level}${target.priceSubject === 'option_premium' ? ' (option premium)' : ''}`;
};

export const sourceLink = (value: string | null): string | null => {
    if (value === null) return null;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && ['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com'].includes(url.hostname)
            ? url.href
            : null;
    } catch {
        return null;
    }
};
